import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const AdContainer = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  color: white;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  
  @media (max-width: 768px) {
    padding: 10px 15px;
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
`;

const AdContent = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const AdText = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  
  .highlight {
    color: #ffd700;
    font-weight: 800;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const UpgradeButton = styled(motion.button)`
  background: #ffd700;
  color: #2c3e50;
  border: none;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    background: #ffed4e;
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.8rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

const AdBanner = () => {
  const { user, isPremium } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [currentAd, setCurrentAd] = useState(0);

  const ads = [
    {
      text: "🎯 Tired of only 6 wheel segments? Get ",
      highlight: "UNLIMITED",
      suffix: " with Premium!"
    },
    {
      text: "😤 Remove these annoying ads! Upgrade to ",
      highlight: "Premium",
      suffix: " for ad-free gaming!"
    },
    {
      text: "👥 Want more than 4 players? ",
      highlight: "Premium",
      suffix: " supports unlimited players!"
    },
    {
      text: "🔒 Need private rooms? ",
      highlight: "Premium",
      suffix: " includes password protection!"
    },
    {
      text: "💾 Losing your custom wheels? ",
      highlight: "Premium",
      suffix: " saves them forever!"
    }
  ];

  useEffect(() => {
    // Only show ads for logged-in, non-premium users
    if (user && !isPremium) {
      setIsVisible(true);
      
      // Rotate ads every 10 seconds
      const interval = setInterval(() => {
        setCurrentAd(prev => (prev + 1) % ads.length);
      }, 10000);
      
      return () => clearInterval(interval);
    } else {
      setIsVisible(false);
    }
  }, [user, isPremium, ads.length]);

  const handleUpgrade = () => {
    window.open('/payment', '_blank');
  };

  const handleClose = () => {
    setIsVisible(false);
    // Show again after 30 seconds (persistent ads)
    setTimeout(() => {
      if (user && !isPremium) {
        setIsVisible(true);
      }
    }, 30000);
  };

  if (!isVisible || !user || isPremium) {
    return null;
  }

  const ad = ads[currentAd];

  return (
    <AnimatePresence>
      <AdContainer
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        transition={{ duration: 0.3 }}
      >
        <AdContent>
          <AdText>
            {ad.text}
            <span className="highlight">{ad.highlight}</span>
            {ad.suffix}
          </AdText>
        </AdContent>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <UpgradeButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleUpgrade}
          >
            Upgrade Now
          </UpgradeButton>
          
          <CloseButton onClick={handleClose}>
            ×
          </CloseButton>
        </div>
      </AdContainer>
    </AnimatePresence>
  );
};

export default AdBanner; 